package com.eladfinish.bible.contest.screens.browse.tableOfContents

import androidx.compose.ui.graphics.Color
import com.eladfinish.bible.contest.arch.ViewModelStore
import com.eladfinish.bible.contest.constants.ColorConstants
import com.eladfinish.bible.contest.constants.Constants
import com.eladfinish.bible.contest.features.hebrewCalendar.GetCurrentParashaUseCase
import com.eladfinish.bible.contest.navigation.BrowseScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.NavigationCommand
import com.eladfinish.bible.contest.navigation.ShonotScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.ViewContentTabsScreenNavigationDestination
import com.eladfinish.bible.contest.screens.browse.tableOfContents.BrowseColumnsAction.OpenBookmark
import com.eladfinish.bible.contest.screens.browse.tableOfContents.BrowseColumnsAction.OpenReference
import com.eladfinish.bible.contest.screens.browse.tableOfContents.BrowseColumnsAction.OpenRemoteChapterCompletion
import com.eladfinish.bible.contest.screens.browse.tableOfContents.BrowseColumnsAction.TabAction
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.BookmarkData
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.BrowseMode
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.ShonotEntry
import com.eladfinish.bible.contest.screens.browse.usecases.BuildChapterListUseCase
import com.eladfinish.bible.contest.screens.browse.usecases.GetBooksSelectionUseCase
import com.eladfinish.bible.contest.screens.browse.usecases.ParseSedarimUseCase
import com.eladfinish.bible.contest.screens.mikud.model.Selection
import com.eladfinish.bible.contest.utils.GetParashotUseCase
import com.russhwolf.settings.Settings
import dev.icerock.moko.resources.desc.Raw
import dev.icerock.moko.resources.desc.StringDesc
import dev.icerock.moko.resources.desc.desc
import io.github.aakira.napier.Napier
import kotlinx.collections.immutable.toImmutableList
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.withContext

class BrowseColumnsStore(
    private val isCompleteChapter: Boolean,
    private val settings: Settings,
    private val getBooksSelectionUseCase: GetBooksSelectionUseCase,
    private val getParashotUseCase: GetParashotUseCase,
    private val parseSedarimUseCase: ParseSedarimUseCase,
    private val buildChapterListUseCase: BuildChapterListUseCase,
    private val getCurrentParashaUseCase: GetCurrentParashaUseCase,
) : ViewModelStore<BrowseColumnsState, BrowseColumnsAction, BrowseColumnsEffect>(
    tag = "BrowseStore",
) {

    private val week by lazy { settings.getInt(Constants.PREF_MY_TESTING_WEEK, 0) }

    // Must come before `init` below: init launches a coroutine (via buildTabs -> launchEffect)
    // on Dispatchers.Default, a real thread pool on JVM targets (desktop in particular), which can
    // start running and touch stateFlow before the constructor reaches a later property
    // initializer in source order. Kotlin runs property initializers and init blocks strictly in
    // declaration order, so this is a plain reordering fix, not a behavior change.
    override val stateFlow = MutableStateFlow(
        BrowseColumnsState(
            isLoading = true,
            tabStates = emptyList<BrowseTabUiState>().toImmutableList(),
        )
    )

    init {
        buildTabs()
    }

    override fun handleAction(action: BrowseColumnsAction): BrowseColumnsState =
        when (action) {
            is TabAction -> {
                handleTabAction(
                    mode = action.mode,
                    tabAction = action.action,
                )
            }

            is OpenReference -> {
                handleOpenReference(
                    bookIndex = action.bookIndex,
                    chapterIndex = action.chapterIndex,
                    verse = action.verse,
                )
            }

            is OpenBookmark -> {
                handleOpenBookmark(action.bookmarkData)
            }

            is OpenRemoteChapterCompletion -> {
                handleOpenRemoteChapterCompletion(action.bookIndex, action.chapterIndex, action.completionDetails)
            }

        }

    private fun buildTabs() {
        launchEffect {
            val tabStates = withContext(Dispatchers.Default) {
                BrowseMode.entries.map { mode ->
                    Napier.d("BrowseStore_init_mode $mode")
                    buildTabInitialState(mode)
                }.toImmutableList()
            }

            updateState {
                it.copy(
                    isLoading = false,
                    tabStates = tabStates,
                )
            }
        }
    }

    private fun buildTabInitialState(mode: BrowseMode): BrowseTabUiState {
        val bookList = when (mode) {
            BrowseMode.SHONOT_MODE -> ShonotEntry.entries.map { it.title.desc() }
            else -> mode.items().map { StringDesc.Raw(it) }
        }

        val bookColorList = when (mode) {
            BrowseMode.BIBLE_MODE -> {
                getBooksSelectionUseCase().map {
                    when (it) {
                        Selection.NONE -> ColorConstants.RED2
                        Selection.PARTIAL -> ColorConstants.BLACK
                        Selection.ALL -> ColorConstants.GREEN2
                    }
                }.toImmutableList()
            }

            BrowseMode.CHUMASH_MODE,
            BrowseMode.SEDARIM_MODE,
            BrowseMode.SHONOT_MODE,
                -> {
                List(bookList.size) { Color.Black }.toImmutableList()
            }
        }

        return BrowseTabUiState(
            mode = mode,
            week = week,
            bookList = bookList.toImmutableList(),
            bookColorList = bookColorList,
            indexOfPickedBook = -1,
            currentParasha = getCurrentParashaUseCase(),
            secondaryList = emptyList<Pair<StringDesc, Boolean>>().toImmutableList(),
        )
    }

    private fun handleTabAction(
        mode: BrowseMode,
        tabAction: BrowseTabAction,
    ): BrowseColumnsState {
        val currentTabState = currentState.tabStates[mode.ordinal]

        // Without books (the "שונות" tab) an item is a part: it opens at once.
        if (tabAction is BrowseTabAction.BookClicked && !mode.hasBooks) {
            return onlyEffect(
                BrowseColumnsEffect.OpenContent(
                    navigation = getDestination(
                        completeChapter = isCompleteChapter,
                        bookmarkData = BookmarkData(
                            browseMode = mode,
                            bookIndex = 0,
                            partIndex = tabAction.index,
                            bookmarkScrollY = 0,
                            showOnqelos = false,
                        ),
                        replaceBrowseInBackStack = false,
                    )
                )
            )
        }

        val newTabState = when (tabAction) {
            is BrowseTabAction.BookClicked -> {
                val newSecondaryList = when (currentTabState.mode) {
                    BrowseMode.BIBLE_MODE -> buildChapterListUseCase(tabAction.index)
                    BrowseMode.CHUMASH_MODE -> parseParashot(bookNumber = tabAction.index + 1).map { it to true }
                    BrowseMode.SEDARIM_MODE -> parseSedarimUseCase(bookNumber = tabAction.index).map { it.name }.map { it to true }
                    BrowseMode.SHONOT_MODE -> emptyList()
                }.toImmutableList()

                currentTabState.copy(
                    indexOfPickedBook = tabAction.index,
                    secondaryList = newSecondaryList,
                ).also { updatedState ->
                    if (tabAction.longClick && updatedState.secondaryList.isNotEmpty()) {
                        launchEffect {
                            sideEffectFlow.emit(
                                BrowseColumnsEffect.OpenContent(
                                    navigation = getDestination(
                                        completeChapter = isCompleteChapter,
                                        bookmarkData = BookmarkData(
                                            browseMode = currentTabState.mode,
                                            bookIndex = tabAction.index,
                                            partIndex = 0,
                                            bookmarkScrollY = 0,
                                            showOnqelos = false,
                                        ),
                                        replaceBrowseInBackStack = false,
                                    )
                                )
                            )
                        }
                    }
                }
            }

            is BrowseTabAction.PartClicked -> {
                launchEffect {
                    sideEffectFlow.emit(
                        BrowseColumnsEffect.OpenContent(
                            navigation = getDestination(
                                completeChapter = isCompleteChapter,
                                bookmarkData = BookmarkData(
                                    browseMode = currentTabState.mode,
                                    bookIndex = currentTabState.indexOfPickedBook,
                                    partIndex = tabAction.index,
                                    bookmarkScrollY = 0,
                                    showOnqelos = false,
                                ),
                                replaceBrowseInBackStack = false,
                            )
                        )
                    )
                }
                currentTabState
            }

            BrowseTabAction.CurrentParashaClicked -> {
                onlyEffect(
                    BrowseColumnsEffect.OpenContent(
                        navigation = getDestination(
                            completeChapter = isCompleteChapter,
                            bookmarkData = BookmarkData(
                                browseMode = BrowseMode.CHUMASH_MODE,
                                bookIndex = currentTabState.currentParasha.bookIndex,
                                partIndex = currentTabState.currentParasha.partIndex,
                                bookmarkScrollY = 0,
                                showOnqelos = false,
                            ),
                            replaceBrowseInBackStack = false,
                        )
                    )
                )
                currentTabState
            }
        }

        return currentState.copy(
            tabStates = currentState.tabStates
                .toMutableList()
                .also { it[mode.ordinal] = newTabState }
                .toImmutableList()
        )
    }

    private fun parseParashot(bookNumber: Int, connected: Boolean = true): List<StringDesc> {
        return getParashotUseCase.parseParashot(bookNumber = bookNumber, connected = connected)
            .parashotNames.map { StringDesc.Raw(it) }
    }

    private fun getDestination(
        completeChapter: Boolean,
        bookmarkData: BookmarkData,
        replaceBrowseInBackStack: Boolean,
        verse: Int = 0,
    ): NavigationCommand.Navigate {
        return when (bookmarkData.browseMode) {
            BrowseMode.BIBLE_MODE -> {
                ViewContentTabsScreenNavigationDestination(
                    browseMode = BrowseMode.BIBLE_MODE.name,
                    isCompleteChapter = completeChapter,
                    indexPickedBook = bookmarkData.bookIndex.plus(1),
                    indexPickedPart = bookmarkData.partIndex.plus(1),
                    bookmarkScrollY = bookmarkData.bookmarkScrollY,
                    bookmarkTextOffset = bookmarkData.bookmarkTextOffset,
                    showOnqelos = bookmarkData.showOnqelos,
                    completionDetails = null,
                    verse = verse,
                ).let {
                    if (replaceBrowseInBackStack) {
                        // No launchSingleTop: with a chapter under this screen (a link opened while reading),
                        // the pop leaves it on top and it would be reused for the new chapter, with the store
                        // of the old one and without clearing this screen's loader.
                        NavigationCommand.Navigate(destination = it) {
                            popUpTo<BrowseScreenNavigationDestination> { inclusive = true }
                        }
                    } else {
                        NavigationCommand.Navigate(destination = it)
                    }
                }
            }

            BrowseMode.CHUMASH_MODE,
            BrowseMode.SEDARIM_MODE,
                -> {
                ViewContentTabsScreenNavigationDestination(
                    browseMode = bookmarkData.browseMode.name,
                    indexPickedBook = bookmarkData.bookIndex.plus(1),
                    indexPickedPart = bookmarkData.partIndex.plus(1),
                    bookmarkScrollY = bookmarkData.bookmarkScrollY,
                    bookmarkTextOffset = bookmarkData.bookmarkTextOffset,
                    showOnqelos = bookmarkData.showOnqelos,
                ).let(NavigationCommand::Navigate)
            }

            BrowseMode.SHONOT_MODE -> {
                ShonotScreenNavigationDestination(
                    entry = ShonotEntry.entries[bookmarkData.partIndex].slug,
                ).let(NavigationCommand::Navigate)
            }
        }
    }

    private fun handleOpenReference(
        bookIndex: Int,
        chapterIndex: Int,
        verse: Int,
    ): BrowseColumnsState {
        return onlyEffect(
            BrowseColumnsEffect.OpenContent(
                navigation = getDestination(
                    completeChapter = false,
                    bookmarkData = BookmarkData(
                        browseMode = BrowseMode.BIBLE_MODE,
                        bookIndex = bookIndex,
                        partIndex = chapterIndex - 1,
                        bookmarkScrollY = 0,
                        showOnqelos = false,
                    ),
                    replaceBrowseInBackStack = true,
                    verse = verse,
                )
            )
        )
    }

    private fun handleOpenBookmark(bookmarkData: BookmarkData): BrowseColumnsState {
        return onlyEffect(
            BrowseColumnsEffect.OpenContent(
                navigation = getDestination(
                    completeChapter = false,
                    bookmarkData = BookmarkData(
                        browseMode = bookmarkData.browseMode,
                        bookIndex = bookmarkData.bookIndex,
                        partIndex = bookmarkData.partIndex,
                        bookmarkScrollY = bookmarkData.bookmarkScrollY,
                        bookmarkTextOffset = bookmarkData.bookmarkTextOffset,
                        showOnqelos = bookmarkData.showOnqelos,
                    ),
                    replaceBrowseInBackStack = false,
                )
            )
        )
    }

    private fun handleOpenRemoteChapterCompletion(
        bookIndex: Int,
        chapterIndex: Int,
        completionDetails: String,
    ): BrowseColumnsState {
        return onlyEffect(
            BrowseColumnsEffect.OpenContent(
                navigation = ViewContentTabsScreenNavigationDestination(
                    browseMode = BrowseMode.BIBLE_MODE.name,
                    indexPickedBook = bookIndex,
                    indexPickedPart = chapterIndex,
                    completionDetails = completionDetails,
                    isCompleteChapter = true,
                ).let(NavigationCommand::Navigate)
            )
        )
    }

    companion object Companion {
        const val TEXT_SIZE = 15.0f
        const val SEEK_BAR_PROGRESS_TEXT_SIZE = 7
    }
}
